/**
 * overlay-dimension-mask.ts — the per-GateDimension overlay MASK for the PDF
 * `/editor` a11y-view (Epic a11y-vlm-staging-gate-261009, Phase 4).
 *
 * WHAT: an ADDITIVE `?overlayDim=<GateDimension>` boot flag that renders the
 * page with ONLY one gate-dimension's overlay construct layer visible — the
 * §5 masked-pass discipline transplanted to the visual channel (one focused
 * query per rubric dimension, only that dimension's overlay visible). This is
 * the producer seam Phase 5's masked VLM judge consumes: one capture per
 * (page × dimension) with only that dimension's overlay layer showing.
 *
 * HOW (zero render-path change — CSS mask over the stable overlay DOM contract):
 * the overlay renderer (`overlays.ts`) already classes every bbox DOM node
 * `.editor-overlay-bbox.cat-<category>` (the 8-bucket `OVERLAY_PALETTE`
 * grouping), with `.editor-overlay-order-chip` for the numbered reading-order
 * chips. A dimension mask is therefore a scoped `<style>` that hides the
 * overlay nodes NOT in the dimension's category set — no new overlay rendering
 * code, no change to the full-overlay view. The style is class-based, so it
 * applies to current AND future (re-rendered on zoom/edit) overlay nodes
 * without re-running. Absent the param the editor is byte-for-byte unchanged.
 *
 * The design (`docs/design/a11y-vlm-staging-gate-261009.md` §3.3) names CSS-only
 * masking the primary path and a `?overlayFilter=` param the sanctioned
 * fallback "if CSS-only masking is brittle". Phase 4 lands the param form as a
 * first-class editor capability: the mask logic is co-located with the class
 * names it depends on (so it cannot silently drift from the renderer, unlike a
 * harness-injected CSS string), and the capability is deterministic + URL-
 * addressable for the headless capture harness (`overlay_capture.py`).
 *
 * @module editor/overlay-dimension-mask
 */
/** The closed set — single place to test membership + iterate. */
export const GATE_DIMENSIONS = Object.freeze([
    "READING_ORDER", "ALT_TEXT", "HEADINGS", "TEXT_RETRIEVAL", "TABLES", "LISTS",
]);
/**
 * GateDimension → overlay mask. Derived from the dimension→census-family map in
 * `a11y_gate_selection.py` (READING_ORDER→orderable, ALT_TEXT→figure,
 * HEADINGS→heading, TEXT_RETRIEVAL→text, TABLES→table, LISTS→list) crossed with
 * the `OVERLAY_PALETTE` category vocabulary (→ A.5: a typed closed map, not an
 * if/else cascade).
 */
const _DIMENSION_MASK = Object.freeze({
    READING_ORDER: { kind: "all" },
    ALT_TEXT: { kind: "categories", categories: ["figure"] },
    HEADINGS: { kind: "categories", categories: ["heading"] },
    TABLES: { kind: "categories", categories: ["table"] },
    LISTS: { kind: "categories", categories: ["list"] },
    TEXT_RETRIEVAL: { kind: "bare" },
});
/** The `<style>` element id — stable so re-application is idempotent. */
export const OVERLAY_DIM_MASK_STYLE_ID = "a11y-overlay-dim-mask";
/** True when `s` names a gate dimension (narrowing guard for the URL param). */
export function isGateDimension(s) {
    return s != null && GATE_DIMENSIONS.includes(s);
}
/** Parse `?overlayDim=<DIM>` from a search string; null when absent/invalid. */
export function overlayDimFromSearch(search) {
    const raw = new URLSearchParams(search).get("overlayDim");
    return isGateDimension(raw) ? raw : null;
}
/**
 * The CSS text that masks the overlay layer down to one dimension's construct.
 * Pure (no DOM) so it is unit-testable without a browser. The rules target the
 * overlay DOM contract from `overlays.ts`:
 *   - `.editor-overlay-bbox.cat-<category>` — the per-category bbox nodes
 *   - `.editor-overlay-order-chip` — the numbered reading-order chips
 */
export function cssForDimension(dim) {
    const spec = _DIMENSION_MASK[dim];
    if (spec.kind === "all") {
        // READING_ORDER: full overlay + chips; nothing hidden.
        return "";
    }
    if (spec.kind === "bare") {
        // TEXT_RETRIEVAL: hide the entire overlay layer (bare base render).
        return ".editor-overlay-bbox { display: none !important; }";
    }
    // categories: hide every bbox NOT in the allowed category set, and hide the
    // order chips (only READING_ORDER emphasizes them). The `:not()` chain keeps
    // the allowed categories visible.
    const notChain = spec.categories.map((c) => `:not(.cat-${c})`).join("");
    return [
        `.editor-overlay-bbox${notChain} { display: none !important; }`,
        ".editor-overlay-order-chip { display: none !important; }",
    ].join("\n");
}
/**
 * Inject (or replace) the dimension-mask `<style>` in `doc`. Idempotent —
 * re-applying with a different dimension swaps the rules in place (same id).
 * `kind:"all"` (READING_ORDER) emits an empty style so the full overlay shows;
 * the element is still present so a subsequent swap to a filtering dimension
 * has a node to update. Returns the style element (for tests/diagnostics).
 */
export function applyOverlayDimensionMask(doc, dim) {
    let style = doc.getElementById(OVERLAY_DIM_MASK_STYLE_ID);
    if (!style) {
        style = doc.createElement("style");
        style.id = OVERLAY_DIM_MASK_STYLE_ID;
        (doc.head ?? doc.documentElement).appendChild(style);
    }
    style.dataset.overlayDim = dim;
    style.textContent = cssForDimension(dim);
    return style;
}
/**
 * Boot hook: if `search` carries a valid `?overlayDim=`, apply its mask to
 * `doc` and return the dimension; else no-op and return null. Called once at
 * editor boot (the full-overlay view is untouched when the param is absent).
 */
export function applyOverlayDimensionMaskFromSearch(doc, search) {
    const dim = overlayDimFromSearch(search);
    if (dim === null)
        return null;
    applyOverlayDimensionMask(doc, dim);
    return dim;
}
